/**
 * Belgian provinces (plus the Brussels-Capital Region, which is not a province
 * but is the eleventh answer everyone expects). Values match
 * `App\DTO\Enums\Project\Province` on the backend.
 */
export enum Province {
  Antwerp = 'ANTWERP',
  EastFlanders = 'EAST_FLANDERS',
  FlemishBrabant = 'FLEMISH_BRABANT',
  Limburg = 'LIMBURG',
  WestFlanders = 'WEST_FLANDERS',
  WalloonBrabant = 'WALLOON_BRABANT',
  Hainaut = 'HAINAUT',
  Liege = 'LIEGE',
  Luxembourg = 'LUXEMBOURG',
  Namur = 'NAMUR',
  Brussels = 'BRUSSELS',
}

export const PROVINCE_LABELS: Record<Province, string> = {
  [Province.Antwerp]: 'Antwerp',
  [Province.EastFlanders]: 'East Flanders',
  [Province.FlemishBrabant]: 'Flemish Brabant',
  [Province.Limburg]: 'Limburg',
  [Province.WestFlanders]: 'West Flanders',
  [Province.WalloonBrabant]: 'Walloon Brabant',
  [Province.Hainaut]: 'Hainaut',
  [Province.Liege]: 'Liège',
  [Province.Luxembourg]: 'Luxembourg',
  [Province.Namur]: 'Namur',
  [Province.Brussels]: 'Brussels-Capital',
};

/** Selection order: the five Flemish provinces, the five Walloon ones, Brussels. */
export const PROVINCES: Province[] = [
  Province.Antwerp,
  Province.EastFlanders,
  Province.FlemishBrabant,
  Province.Limburg,
  Province.WestFlanders,
  Province.WalloonBrabant,
  Province.Hainaut,
  Province.Liege,
  Province.Luxembourg,
  Province.Namur,
  Province.Brussels,
];

export const provinceLabel = (province?: Province | null): string =>
  province ? PROVINCE_LABELS[province] ?? province : '';

/**
 * Inclusive postal-code ranges per province, in allocation order. Belgium hands
 * out its four-digit codes in contiguous blocks, so the number alone identifies
 * the province exactly — Flemish Brabant and Hainaut simply own two blocks each.
 *
 * Mirrors `App\Services\BelgianProvinceResolver` on the backend; keep the two in
 * step.
 */
const POSTAL_CODE_RANGES: ReadonlyArray<readonly [number, number, Province]> = [
  [1000, 1299, Province.Brussels],
  [1300, 1499, Province.WalloonBrabant],
  [1500, 1999, Province.FlemishBrabant],
  [2000, 2999, Province.Antwerp],
  [3000, 3499, Province.FlemishBrabant],
  [3500, 3999, Province.Limburg],
  [4000, 4999, Province.Liege],
  [5000, 5999, Province.Namur],
  [6000, 6599, Province.Hainaut],
  [6600, 6999, Province.Luxembourg],
  [7000, 7999, Province.Hainaut],
  [8000, 8999, Province.WestFlanders],
  [9000, 9999, Province.EastFlanders],
];

/**
 * null for anything that isn't a readable Belgian postal code. "2020", "B-2020"
 * and " 2020 " are the same code; "1012 AB" (Dutch) and "L-1234" (Luxembourgish)
 * are four digits inside another country's format and must not pass.
 */
export function provinceFromPostalCode(
  postalCode: string | null | undefined,
): Province | null {
  const normalized = (postalCode ?? '')
    .trim()
    .toUpperCase()
    .replace(/^BE?[- ]?/, '');
  if (!/^\d{4}$/.test(normalized)) {
    return null;
  }

  const code = Number(normalized);
  const range = POSTAL_CODE_RANGES.find(([from, to]) => code >= from && code <= to);

  return range ? range[2] : null;
}

interface GeocoderAddressComponent {
  readonly types?: readonly string[];
  readonly long_name?: string;
  readonly short_name?: string;
}

/** Country names Google returns for Belgium, in the languages the app asks for. */
const BELGIAN_COUNTRY_NAMES = [
  'belgium',
  'belgië',
  'belgie',
  'belgique',
  'belgien',
  'be',
  'bel',
];

/**
 * Province read off a formatted address ("Schaarbeeklei 647, 1800 Vilvoorde,
 * Belgium"), for the results that carry no address components at all.
 *
 * The Places `findPlaceFromQuery` call behind the location search cannot return
 * `address_components` — it is not one of the fields that method supports — so a
 * location picked from the search box only ever has its formatted address. A
 * map click goes through the geocoder and does have the components.
 *
 * Strict on purpose: the last segment must name Belgium and the one before it
 * must start with the postal code, which is how Google formats every Belgian
 * address. Anything else resolves to null rather than risk reading a house
 * number, or a foreign code, as a province.
 */
export function provinceFromFormattedAddress(
  address: string | null | undefined,
): Province | null {
  const segments = (address ?? '')
    .split(',')
    .map(segment => segment.trim())
    .filter(Boolean);
  if (segments.length < 2) {
    return null;
  }

  const country = segments[segments.length - 1].toLowerCase();
  if (!BELGIAN_COUNTRY_NAMES.includes(country)) {
    return null;
  }

  // "1800 Vilvoorde" — the postal code opens the segment before the country.
  const locality = segments[segments.length - 2];
  const match = /^(?:BE?[- ]?)?(\d{4})(?:\s|$)/.exec(locality.toUpperCase());

  return match ? provinceFromPostalCode(match[1]) : null;
}

/**
 * Province of a location picked in the map/autocomplete, read off the Google
 * geocoder result's postal_code component, and off its formatted address when
 * the result carries no components. Addresses that name a country other than
 * Belgium resolve to null.
 *
 * Mirrors `App\Services\BelgianProvinceResolver::fromAddress()`.
 */
export function provinceFromRawData(
  rawData: Record<string, unknown> | string | null | undefined,
): Province | null {
  if (!rawData) {
    return null;
  }

  let parsed: unknown = rawData;
  if (typeof rawData === 'string') {
    try {
      parsed = JSON.parse(rawData);
    } catch {
      return null;
    }
  }

  const formattedAddress = (parsed as { formatted_address?: unknown })?.formatted_address;
  const components = (parsed as { address_components?: unknown })?.address_components;
  if (!Array.isArray(components)) {
    return typeof formattedAddress === 'string'
      ? provinceFromFormattedAddress(formattedAddress)
      : null;
  }

  const find = (type: string): GeocoderAddressComponent | undefined =>
    (components as GeocoderAddressComponent[]).find(component =>
      component?.types?.includes(type),
    );

  const country = find('country');
  const countryName = country?.long_name ?? country?.short_name;
  if (countryName && !BELGIAN_COUNTRY_NAMES.includes(countryName.trim().toLowerCase())) {
    return null;
  }

  const postalCode = find('postal_code');
  const fromComponents = provinceFromPostalCode(
    postalCode?.long_name ?? postalCode?.short_name,
  );
  if (fromComponents) {
    return fromComponents;
  }

  // Components present but no readable postal code among them.
  return typeof formattedAddress === 'string'
    ? provinceFromFormattedAddress(formattedAddress)
    : null;
}
